import React, { useState, useEffect } from 'react';
import { useToast } from '../../Toast';
import { useAuth } from '../../../contexts/AuthContext';
import { db } from '../../../config/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import BillingProfileModal from './BillingProfileModal';
import EditDefaultBillingModal from './EditDefaultBillingModal';
import { useConfirm } from '../../../hooks/useConfirm';

interface BillingProfile {
  id: string;
  rule: {
    clinicianId?: string;
    clinicianName?: string;
    payerId?: string;
    payerName?: string;
  };
  providerType: 'individual' | 'organization';
  firstName?: string;
  middleName?: string;
  lastName?: string;
  organizationName?: string;
  taxIdOrSSN: string;
  npi: string;
  taxonomyCode: string;
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
  };
}

const InsuranceBilling: React.FC = () => {
  const toast = useToast();
  const { user } = useAuth();
  const { confirm, ConfirmDialog } = useConfirm();
  // Automatic status checks
  const [automaticStatusChecks, setAutomaticStatusChecks] = useState(false);

  // Claim and superbill settings
  const [taxIdOrSSN, setTaxIdOrSSN] = useState('');
  const [displayTaxIdOn, setDisplayTaxIdOn] = useState<'superbills_and_invoices' | 'superbills' | 'none'>('superbills_and_invoices');
  const [organizationNPI, setOrganizationNPI] = useState('');
  const [enableModifiers, setEnableModifiers] = useState(false);
  const [includeMultipleDiagnosisCodes, setIncludeMultipleDiagnosisCodes] = useState(false);

  // Service Facility Location (Box 32)
  const [includeOnClaimForm, setIncludeOnClaimForm] = useState(false);
  const [otherIdType, setOtherIdType] = useState<'0B' | 'G2' | 'LU' | ''>('');
  const [otherIdValue, setOtherIdValue] = useState('');

  // Default billing provider
  const [defaultProvider] = useState({
    name: '',
    npi: '',
    taxonomyCode: '',
    address: ''
  });

  // Billing profiles
  const [billingProfiles, setBillingProfiles] = useState<BillingProfile[]>([]);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Modals
  const [showBillingProfileModal, setShowBillingProfileModal] = useState(false);
  const [showDefaultBillingModal, setShowDefaultBillingModal] = useState(false);
  const [editingProfile, setEditingProfile] = useState<BillingProfile | null>(null);

  // Tooltips
  const [showModifiersTooltip, setShowModifiersTooltip] = useState(false);
  const [showDiagnosisTooltip, setShowDiagnosisTooltip] = useState(false);
  const [showTaxIdTooltip, setShowTaxIdTooltip] = useState(false);
  const [showOrgNPITooltip, setShowOrgNPITooltip] = useState(false);
  const [showServiceFacilityTooltip, setShowServiceFacilityTooltip] = useState(false);
  const [showOtherIdTooltip, setShowOtherIdTooltip] = useState(false);

  // Load settings from Firestore
  useEffect(() => {
    if (!user) return;
    const loadSettings = async () => {
      try {
        const docRef = doc(db, 'billing_settings', 'insurance_billing');
        const snap = await getDoc(docRef);
        if (snap.exists()) {
          const data = snap.data();
          if (data.automaticStatusChecks) {
            setAutomaticStatusChecks(data.automaticStatusChecks.enabled || false);
          }
          if (data.claimSettings) {
            setTaxIdOrSSN(data.claimSettings.taxIdOrSSN || '');
            setDisplayTaxIdOn(data.claimSettings.displayTaxIdOn || 'superbills_and_invoices');
            setOrganizationNPI(data.claimSettings.organizationNPI || '');
            setEnableModifiers(data.claimSettings.enableModifiers || false);
            setIncludeMultipleDiagnosisCodes(data.claimSettings.includeMultipleDiagnosisCodes || false);
          }
          if (data.serviceFacilityLocation) {
            setIncludeOnClaimForm(data.serviceFacilityLocation.includeOnClaimForm || false);
            setOtherIdType(data.serviceFacilityLocation.otherIdType || '');
            setOtherIdValue(data.serviceFacilityLocation.otherIdValue || '');
          }
        }
      } catch (err) {
        console.error('Error loading insurance billing settings:', err);
      }
    };
    loadSettings();
  }, [user]);

  const handleSaveChanges = async () => {
    const settings = {
      automaticStatusChecks: {
        enabled: automaticStatusChecks,
        daysSinceLastCheck: 90
      },
      claimSettings: {
        taxIdOrSSN,
        displayTaxIdOn,
        organizationNPI,
        enableModifiers,
        includeMultipleDiagnosisCodes
      },
      serviceFacilityLocation: {
        includeOnClaimForm,
        otherIdType: otherIdType || '',
        otherIdValue: otherIdValue || ''
      },
      ownerId: user?.uid || '',
      updatedAt: new Date().toISOString()
    };

    try {
      const docRef = doc(db, 'billing_settings', 'insurance_billing');
      await setDoc(docRef, settings, { merge: true });
      toast.success('Insurance billing settings saved successfully!');
    } catch (err) {
      console.error('Error saving insurance billing settings:', err);
      toast.error('Failed to save insurance billing settings.');
    }
  };

  const handleAddBillingProfile = () => {
    setEditingProfile(null);
    setShowBillingProfileModal(true);
  };

  const handleEditBillingProfile = (profile: BillingProfile) => {
    setEditingProfile(profile);
    setShowBillingProfileModal(true);
  };

  const handleSaveBillingProfile = (profile: BillingProfile) => {
    if (editingProfile) {
      // Update existing profile
      setBillingProfiles(prev => prev.map(p => p.id === profile.id ? profile : p));
    } else {
      // Add new profile
      setBillingProfiles(prev => [...prev, { ...profile, id: Date.now().toString() }]);
    }
    setShowBillingProfileModal(false);
    setEditingProfile(null);
  };

  const handleDeleteBillingProfile = async (profileId: string) => {
    if (await confirm({ title: 'Delete Billing Profile', message: 'Are you sure you want to delete this billing profile?', confirmLabel: 'Delete', variant: 'danger' })) {
      setBillingProfiles(prev => prev.filter(p => p.id !== profileId));
    }
  };

  return (
    <div style={{ padding: '32px', maxWidth: '1200px' }}>
      {/* Page Header with Save Button */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: 700, color: '#FFFFFF', margin: '0 0 4px 0' }}>
            Insurance billing
          </h1>
          <p style={{ fontSize: '13px', color: '#87CEEB', margin: 0 }}>
            Manage claims and superbills
          </p>
        </div>
        <button
          onClick={handleSaveChanges}
          style={{
            backgroundColor: '#1E90FF',
            color: '#FFFFFF',
            border: 'none',
            borderRadius: '6px',
            padding: '8px 20px',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          Save changes
        </button>
      </div>

      {/* Video Link */}
      <div style={{ marginBottom: '20px', textAlign: 'right' }}>
        <span
          style={{
            color: '#1E90FF',
            fontSize: '13px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px'
          }}
        >
          <span>📺</span> Watch a quick video about Insurance documents
        </span>
      </div>

      {/* Automatic Status Checks */}
      <section style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '18px', fontWeight: 600, color: '#FFFFFF', marginBottom: '12px' }}>
          Automatic status checks
        </h2>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer', color: '#E0E0E0' }}>
          <input
            type="checkbox"
            checked={automaticStatusChecks}
            onChange={(e) => setAutomaticStatusChecks(e.target.checked)}
            style={{ marginTop: '2px', cursor: 'pointer', width: '18px', height: '18px' }}
          />
          <span style={{ fontSize: '14px', lineHeight: '1.5' }}>
            Run a status check for clients who haven't had a successful insurance check or claim in the last 90 days.
          </span>
        </label>
      </section>

      {/* Claim and Superbill Settings */}
      <section style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '18px', fontWeight: 600, color: '#FFFFFF', marginBottom: '16px' }}>
          Claim and superbill settings
        </h2>

        {/* Tax ID or SSN + Display TaxID dropdown */}
        <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: '16px', marginBottom: '16px', alignItems: 'start' }}>
          <div style={{ position: 'relative' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: '#E0E0E0', marginBottom: '8px' }}>
              Tax ID or SSN
              <span
                onMouseEnter={() => setShowTaxIdTooltip(true)}
                onMouseLeave={() => setShowTaxIdTooltip(false)}
                style={{ color: '#87CEEB', cursor: 'help', fontSize: '12px' }}
              >
                ⓘ
              </span>
            </label>
            <input
              type="text"
              value={taxIdOrSSN}
              onChange={(e) => setTaxIdOrSSN(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                backgroundColor: '#0F4C75',
                border: '1px solid #6CA6C1',
                borderRadius: '4px',
                color: '#FFFFFF',
                fontSize: '14px'
              }}
            />
            {showTaxIdTooltip && (
              <div style={{
                position: 'absolute',
                top: '0px',
                left: '210px',
                backgroundColor: '#2C2C2C',
                border: '1px solid #4A4A4A',
                borderRadius: '6px',
                padding: '12px 16px',
                fontSize: '13px',
                color: '#FFFFFF',
                width: '320px',
                zIndex: 1000,
                boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
                lineHeight: '1.5'
              }}>
                <div style={{
                  position: 'absolute',
                  left: '-6px',
                  top: '12px',
                  width: '0',
                  height: '0',
                  borderTop: '6px solid transparent',
                  borderBottom: '6px solid transparent',
                  borderRight: '6px solid #4A4A4A'
                }} />
                Your Tax ID or SSN will be used for billing and insurance claims.
              </div>
            )}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '14px', color: '#E0E0E0', marginBottom: '8px' }}>
              Display TaxID or SSN on:
            </label>
            <select
              value={displayTaxIdOn}
              onChange={(e) => setDisplayTaxIdOn(e.target.value as any)}
              style={{
                width: '100%',
                maxWidth: '300px',
                padding: '8px 12px',
                backgroundColor: '#0F4C75',
                border: '1px solid #6CA6C1',
                borderRadius: '4px',
                color: '#FFFFFF',
                fontSize: '14px'
              }}
            >
              <option value="superbills_and_invoices">Superbills and Invoices</option>
              <option value="superbills">Superbills</option>
              <option value="none">None</option>
            </select>
          </div>
        </div>

        {/* Organization NPI */}
        <div style={{ marginBottom: '16px', position: 'relative' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: '#E0E0E0', marginBottom: '8px' }}>
            Organization NPI
            <span
              onMouseEnter={() => setShowOrgNPITooltip(true)}
              onMouseLeave={() => setShowOrgNPITooltip(false)}
              style={{ color: '#87CEEB', cursor: 'help', fontSize: '12px' }}
            >
              ⓘ
            </span>
          </label>
          <input
            type="text"
            value={organizationNPI}
            onChange={(e) => setOrganizationNPI(e.target.value)}
            placeholder="0000000000"
            style={{
              width: '200px',
              padding: '8px 12px',
              backgroundColor: '#0F4C75',
              border: '1px solid #6CA6C1',
              borderRadius: '4px',
              color: '#FFFFFF',
              fontSize: '14px'
            }}
          />
          {showOrgNPITooltip && (
            <div style={{
              position: 'absolute',
              top: '0px',
              left: '210px',
              backgroundColor: '#2C2C2C',
              border: '1px solid #4A4A4A',
              borderRadius: '6px',
              padding: '12px 16px',
              fontSize: '13px',
              color: '#FFFFFF',
              width: '320px',
              zIndex: 1000,
              boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
              lineHeight: '1.5'
            }}>
              <div style={{
                position: 'absolute',
                left: '-6px',
                top: '12px',
                width: '0',
                height: '0',
                borderTop: '6px solid transparent',
                borderBottom: '6px solid transparent',
                borderRight: '6px solid #4A4A4A'
              }} />
              Enter your organization's National Provider Identifier (NPI) number for billing purposes.
            </div>
          )}
        </div>

        {/* Enable Modifiers */}
        <div style={{ marginBottom: '16px', position: 'relative' }}>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer', color: '#E0E0E0' }}>
            <input
              type="checkbox"
              checked={enableModifiers}
              onChange={(e) => setEnableModifiers(e.target.checked)}
              style={{ marginTop: '2px', cursor: 'pointer', width: '18px', height: '18px' }}
            />
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px' }}>
              Enable Modifiers for your practice
              <span
                onMouseEnter={() => setShowModifiersTooltip(true)}
                onMouseLeave={() => setShowModifiersTooltip(false)}
                style={{ color: '#87CEEB', cursor: 'help', fontSize: '12px' }}
              >
                ⓘ
              </span>
            </span>
          </label>
          {showModifiersTooltip && (
            <div style={{
              position: 'absolute',
              top: '-50px',
              left: '30px',
              backgroundColor: '#2C2C2C',
              border: '1px solid #4A4A4A',
              borderRadius: '6px',
              padding: '12px 16px',
              fontSize: '13px',
              color: '#FFFFFF',
              width: '380px',
              zIndex: 1000,
              boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
              lineHeight: '1.5'
            }}>
              <div style={{
                position: 'absolute',
                left: '12px',
                top: '-6px',
                width: '0',
                height: '0',
                borderLeft: '6px solid transparent',
                borderRight: '6px solid transparent',
                borderBottom: '6px solid #4A4A4A'
              }} />
              This will allow you to easily add and use modifiers for claim filing.{' '}
              <span style={{ color: '#4A9EFF' }}>Learn more</span>
            </div>
          )}
        </div>

        {/* Include Multiple Diagnosis Codes */}
        <div style={{ position: 'relative' }}>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer', color: '#E0E0E0' }}>
            <input
              type="checkbox"
              checked={includeMultipleDiagnosisCodes}
              onChange={(e) => setIncludeMultipleDiagnosisCodes(e.target.checked)}
              style={{ marginTop: '2px', cursor: 'pointer', width: '18px', height: '18px' }}
            />
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px' }}>
              Include multiple Diagnosis Codes
              <span
                onMouseEnter={() => setShowDiagnosisTooltip(true)}
                onMouseLeave={() => setShowDiagnosisTooltip(false)}
                style={{ color: '#87CEEB', cursor: 'help', fontSize: '12px' }}
              >
                ⓘ
              </span>
            </span>
          </label>
          {showDiagnosisTooltip && (
            <div style={{
              position: 'absolute',
              top: '-70px',
              left: '30px',
              backgroundColor: '#2C2C2C',
              border: '1px solid #4A4A4A',
              borderRadius: '6px',
              padding: '12px 16px',
              fontSize: '13px',
              color: '#FFFFFF',
              width: '420px',
              zIndex: 1000,
              boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
              lineHeight: '1.5'
            }}>
              <div style={{
                position: 'absolute',
                left: '12px',
                top: '-6px',
                width: '0',
                height: '0',
                borderLeft: '6px solid transparent',
                borderRight: '6px solid transparent',
                borderBottom: '6px solid #4A4A4A'
              }} />
              When checked, your claims and superbills will be created with all of the diagnosis codes you added to the sessions
            </div>
          )}
        </div>
      </section>

      {/* Service Facility Location (Box 32) */}
      <section style={{ marginBottom: '32px', position: 'relative' }}>
        <h2 style={{ fontSize: '18px', fontWeight: 600, color: '#FFFFFF', marginBottom: '12px' }}>
          Service Facility Location (Box 32)
        </h2>

        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer', color: '#E0E0E0', marginBottom: '16px' }}>
          <input
            type="checkbox"
            checked={includeOnClaimForm}
            onChange={(e) => setIncludeOnClaimForm(e.target.checked)}
            style={{ marginTop: '2px', cursor: 'pointer', width: '18px', height: '18px' }}
          />
          <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px' }}>
            Include on Claim Form
            <span
              onMouseEnter={() => setShowServiceFacilityTooltip(true)}
              onMouseLeave={() => setShowServiceFacilityTooltip(false)}
              style={{ color: '#87CEEB', cursor: 'help', fontSize: '12px' }}
            >
              ⓘ
            </span>
          </span>
        </label>
        {showServiceFacilityTooltip && (
          <div style={{
            position: 'absolute',
            top: '42px',
            left: '30px',
            backgroundColor: '#2C2C2C',
            border: '1px solid #4A4A4A',
            borderRadius: '6px',
            padding: '12px 16px',
            fontSize: '13px',
            color: '#FFFFFF',
            width: '350px',
            zIndex: 1000,
            boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
            lineHeight: '1.5'
          }}>
            <div style={{
              position: 'absolute',
              left: '12px',
              top: '-6px',
              width: '0',
              height: '0',
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderBottom: '6px solid #4A4A4A'
            }} />
            Include service facility information in Box 32 of the CMS-1500 claim form.
          </div>
        )}

        {includeOnClaimForm && (
          <div style={{ marginLeft: '30px', display: 'flex', gap: '16px', alignItems: 'end', position: 'relative' }}>
            <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: '#E0E0E0', marginBottom: '8px' }}>
                Other ID
                <span
                  onMouseEnter={() => setShowOtherIdTooltip(true)}
                  onMouseLeave={() => setShowOtherIdTooltip(false)}
                  style={{ color: '#87CEEB', cursor: 'help', fontSize: '12px' }}
                >
                  ⓘ
                </span>
              </label>
              {showOtherIdTooltip && (
                <div style={{
                  position: 'absolute',
                  top: '-50px',
                  left: '0px',
                  backgroundColor: '#2C2C2C',
                  border: '1px solid #4A4A4A',
                  borderRadius: '6px',
                  padding: '12px 16px',
                  fontSize: '13px',
                  color: '#FFFFFF',
                  width: '340px',
                  zIndex: 1000,
                  boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
                  lineHeight: '1.5'
                }}>
                  <div style={{
                    position: 'absolute',
                    left: '40px',
                    bottom: '-6px',
                    width: '0',
                    height: '0',
                    borderLeft: '6px solid transparent',
                    borderRight: '6px solid transparent',
                    borderTop: '6px solid #4A4A4A'
                  }} />
                  Additional identifier for the service facility location (e.g., state license number, location number).
                </div>
              )}
              <select
                value={otherIdType}
                onChange={(e) => setOtherIdType(e.target.value as any)}
                style={{
                  width: '280px',
                  padding: '8px 12px',
                  backgroundColor: '#0F4C75',
                  border: '1px solid #6CA6C1',
                  borderRadius: '4px',
                  color: '#FFFFFF',
                  fontSize: '14px'
                }}
              >
                <option value="">Select</option>
                <option value="0B">0B - State license number</option>
                <option value="G2">G2 - Provider commercial number</option>
                <option value="LU">LU - Location number</option>
              </select>
            </div>

            <div>
              <input
                type="text"
                value={otherIdValue}
                onChange={(e) => setOtherIdValue(e.target.value)}
                placeholder="Enter ID value"
                style={{
                  width: '250px',
                  padding: '8px 12px',
                  backgroundColor: '#0F4C75',
                  border: '1px solid #6CA6C1',
                  borderRadius: '4px',
                  color: '#FFFFFF',
                  fontSize: '14px'
                }}
              />
            </div>
          </div>
        )}
      </section>

      {/* Billing Provider Info - Default */}
      <section style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '18px', fontWeight: 600, color: '#FFFFFF', marginBottom: '12px' }}>
          Billing provider info
        </h2>

        <p style={{ fontSize: '14px', fontWeight: 600, color: '#E0E0E0', marginBottom: '12px' }}>
          Default
        </p>

        <div style={{
          backgroundColor: '#0F3D5C',
          border: '1px solid #6CA6C1',
          borderRadius: '8px',
          padding: '16px'
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '24px', alignItems: 'start' }}>
            <div>
              <p style={{ fontSize: '12px', color: '#87CEEB', marginBottom: '4px' }}>Provider name</p>
              <p style={{ fontSize: '14px', color: '#FFFFFF', fontWeight: 500 }}>{defaultProvider.name}</p>
            </div>
            <div>
              <p style={{ fontSize: '12px', color: '#87CEEB', marginBottom: '4px' }}>NPI (Box 33a)</p>
              <p style={{ fontSize: '14px', color: '#FFFFFF', fontWeight: 500 }}>{defaultProvider.npi}</p>
            </div>
            <div>
              <p style={{ fontSize: '12px', color: '#87CEEB', marginBottom: '4px' }}>Taxonomy code (Box 33b)</p>
              <p style={{ fontSize: '14px', color: '#FFFFFF', fontWeight: 500 }}>{defaultProvider.taxonomyCode}</p>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <p style={{ fontSize: '12px', color: '#87CEEB', marginBottom: '8px' }}>Address</p>
              <button
                onClick={() => setShowDefaultBillingModal(true)}
                style={{
                  color: '#1E90FF',
                  backgroundColor: 'transparent',
                  border: 'none',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  padding: 0
                }}
              >
                Edit
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Billing Profiles */}
      <section style={{ marginBottom: '32px' }}>
        <div style={{ marginBottom: '12px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 600, color: '#FFFFFF', marginBottom: '6px' }}>
            Billing Profiles
          </h2>
          <p style={{ fontSize: '13px', color: '#87CEEB', marginBottom: '4px' }}>
            Override default billing info on claims prepared for specific clinicians and payers.{' '}
            <span style={{ color: '#1E90FF' }}>Learn about billing profiles</span>
          </p>
        </div>

        {/* Table */}
        <div style={{
          backgroundColor: '#0F3D5C',
          border: '1px solid #6CA6C1',
          borderRadius: '8px',
          overflow: 'hidden'
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#0A2540', borderBottom: '1px solid #6CA6C1' }}>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px', fontWeight: 600, color: '#87CEEB' }}>
                  Rule
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px', fontWeight: 600, color: '#87CEEB' }}>
                  NPI (Box 33a)
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px', fontWeight: 600, color: '#87CEEB' }}>
                  Taxonomy code (Box 33b)
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px', fontWeight: 600, color: '#87CEEB' }}>
                  Address
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px', fontWeight: 600, color: '#87CEEB', width: '100px' }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {billingProfiles.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ padding: '48px 16px', textAlign: 'center', color: '#87CEEB', fontSize: '14px' }}>
                    No billing profiles yet. Click "Add billing profile" to create one.
                  </td>
                </tr>
              ) : (
                billingProfiles.slice(0, rowsPerPage).map((profile) => (
                  <tr key={profile.id} style={{ borderBottom: '1px solid #2A5470' }}>
                    <td style={{ padding: '16px', fontSize: '14px', color: '#FFFFFF' }}>
                      {profile.rule.clinicianName && profile.rule.payerName
                        ? `${profile.rule.clinicianName} → ${profile.rule.payerName}`
                        : profile.rule.clinicianName
                        ? `${profile.rule.clinicianName} → All payers`
                        : profile.rule.payerName
                        ? `All clinicians → ${profile.rule.payerName}`
                        : 'All claims'}
                    </td>
                    <td style={{ padding: '16px', fontSize: '14px', color: '#FFFFFF' }}>{profile.npi}</td>
                    <td style={{ padding: '16px', fontSize: '14px', color: '#FFFFFF' }}>{profile.taxonomyCode}</td>
                    <td style={{ padding: '16px', fontSize: '14px', color: '#FFFFFF' }}>
                      {profile.address.city}, {profile.address.state}
                    </td>
                    <td style={{ padding: '16px', fontSize: '14px' }}>
                      <button
                        onClick={() => handleEditBillingProfile(profile)}
                        style={{
                          color: '#1E90FF',
                          backgroundColor: 'transparent',
                          border: 'none',
                          fontSize: '13px',
                          cursor: 'pointer',
                          marginRight: '12px'
                        }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteBillingProfile(profile.id)}
                        style={{
                          color: '#FF6B6B',
                          backgroundColor: 'transparent',
                          border: 'none',
                          fontSize: '13px',
                          cursor: 'pointer'
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '14px', color: '#E0E0E0' }}>Rows per page</span>
            <select
              value={rowsPerPage}
              onChange={(e) => setRowsPerPage(Number(e.target.value))}
              style={{
                padding: '6px 12px',
                backgroundColor: '#0F4C75',
                border: '1px solid #6CA6C1',
                borderRadius: '4px',
                color: '#FFFFFF',
                fontSize: '14px'
              }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span style={{ fontSize: '14px', color: '#87CEEB' }}>
              1-{Math.min(rowsPerPage, billingProfiles.length)} of {billingProfiles.length}
            </span>
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            <button style={{
              padding: '6px 12px',
              backgroundColor: '#0F4C75',
              border: '1px solid #6CA6C1',
              borderRadius: '4px',
              color: '#E0E0E0',
              fontSize: '14px',
              cursor: 'pointer'
            }}>
              ⏮
            </button>
            <button style={{
              padding: '6px 12px',
              backgroundColor: '#0F4C75',
              border: '1px solid #6CA6C1',
              borderRadius: '4px',
              color: '#E0E0E0',
              fontSize: '14px',
              cursor: 'pointer'
            }}>
              ◀
            </button>
            <button style={{
              padding: '6px 12px',
              backgroundColor: '#0F4C75',
              border: '1px solid #6CA6C1',
              borderRadius: '4px',
              color: '#E0E0E0',
              fontSize: '14px',
              cursor: 'pointer'
            }}>
              ▶
            </button>
            <button style={{
              padding: '6px 12px',
              backgroundColor: '#0F4C75',
              border: '1px solid #6CA6C1',
              borderRadius: '4px',
              color: '#E0E0E0',
              fontSize: '14px',
              cursor: 'pointer'
            }}>
              ⏭
            </button>
          </div>
        </div>

        {/* Add Billing Profile Button */}
        <button
          onClick={handleAddBillingProfile}
          style={{
            marginTop: '16px',
            padding: '10px 20px',
            backgroundColor: 'transparent',
            border: '1px solid #6CA6C1',
            borderRadius: '6px',
            color: '#FFFFFF',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          Add billing profile
        </button>
      </section>

      {/* Modals */}
      {showBillingProfileModal && (
        <BillingProfileModal
          profile={editingProfile}
          onSave={handleSaveBillingProfile}
          onClose={() => {
            setShowBillingProfileModal(false);
            setEditingProfile(null);
          }}
        />
      )}

      {showDefaultBillingModal && (
        <EditDefaultBillingModal
          onSave={(data) => {
            console.log('Saving default billing info:', data);
            setShowDefaultBillingModal(false);
          }}
          onClose={() => setShowDefaultBillingModal(false)}
        />
      )}
      {ConfirmDialog}
    </div>
  );
};

export default InsuranceBilling;
